import type {
  SmartCVPublicDocument,
  CVConfigData,
  PublicProfilePublicUserPage,
} from "@tabbio/types";
import { normalizeCredentialUrl } from "@tabbio/utils";
import { formatCvDateLabel } from "@tabbio/utils/cv-dates";
import { formatPeriod } from "./cv-mapping";
import {
  DEFAULT_CV_CONFIG,
  type CVConfig,
  type CVProfile,
} from "./cv-mock-data";

export type PublicCvSharePayload = {
  shareLink: {
    id: string;
    label: string | null;
    permissions: string[];
    visibility: Record<string, unknown>;
    allowDownload: boolean;
    isPublic?: boolean;
    expiresAt: string | null;
    shareUrl?: string | null;
    legacyShareUrl?: string | null;
    downloadUrls?: Record<string, string> | null;
  };
  document: SmartCVPublicDocument;
  cvConfig?: CVConfigData | null;
};

export function smartDocumentToCvProfile(
  document: SmartCVPublicDocument,
): CVProfile {
  const socials = document.socials ?? {};
  return {
    name: document.name ?? "",
    username: document.username ?? "",
    headline: document.title ?? "",
    summary: document.summary ?? document.bio ?? "",
    location: document.location ?? "",
    avatarUrl: document.avatarUrl ?? "",
    contactEmail: document.contactEmail ?? "",
    phone: socials.phone ?? "",
    website: socials.website ?? "",
    linkedin: socials.linkedin ?? "",
    github: socials.github ?? "",
    dribbble: socials.dribbble ?? "",
    behance: socials.behance ?? "",
    highlights: (document.highlights ?? []).map((highlight, index) => ({
      id: highlight.id ?? `highlight-${index}`,
      title: highlight.title ?? "",
      desc: highlight.desc ?? "",
    })),
    skillGroups: document.skillGroups ?? [],
    languages: document.languages ?? [],
    projects: document.projects ?? [],
    experience: (document.experiences ?? []).map((experience) => ({
      id: experience.id,
      role: experience.title,
      company: experience.company,
      location: experience.location ?? "",
      locationType: experience.locationType ?? "",
      employmentType: experience.employmentType ?? "",
      period: formatPeriod(
        experience.startDate,
        experience.endDate ?? null,
        experience.current,
      ),
      overview: experience.overview ?? "",
      bullets: experience.bullets ?? [],
    })),
    education: (document.educations ?? []).map((education) => ({
      id: education.id,
      degree: education.degree ?? "",
      school: education.school,
      startDate: education.startDate ?? null,
      year: formatMonthYear(education.endDate),
      description: education.description ?? "",
    })),
    certifications: (document.certifications ?? []).map((certification) => ({
      id: certification.id,
      name: certification.name,
      issuer: certification.issuer ?? "",
      date: certification.issueDate
        ? `Issued ${formatMonthYear(certification.issueDate)}`
        : "",
      credentialUrl: normalizeCredentialUrl(certification.credentialUrl),
    })),
    jobPreferences: {
      openToWork: Boolean(document.openToWork),
      jobTitles: [],
      locationTypes: [],
      preferredLocations: [],
      employmentTypes: [],
    },
  };
}

/**
 * Public profile page -> SmartCV document, so the native CV renderer can show
 * someone else's published CV from the by-handle endpoint.
 *
 * Deliberately mirrors getPublicSmartCV in apps/web/src/lib/smartcv.ts field for
 * field: the same `mainCv ?? page` precedence, so the app and the public web CV
 * render the same document instead of quietly diverging. Only the fields
 * smartDocumentToCvProfile actually reads are populated.
 *
 * Nothing here widens what the endpoint returns. The API has already applied
 * the owner's privacy policy (contactEmail/phone are withheld unless the owner
 * published them), so there is no direct-contact leak to re-filter.
 */
export function publicProfilePageToSmartDocument(
  page: PublicProfilePublicUserPage,
): SmartCVPublicDocument {
  const mainCv = page.mainCv;
  const socials = Object.fromEntries(
    page.socials
      .filter((social) => social.enabled && social.url.trim().length > 0)
      .map((social) => [social.id, social.url]),
  ) as SmartCVPublicDocument["socials"];
  if (page.privacy.showLinks && page.owner.website) {
    socials.website = page.owner.website;
  }

  return {
    username: page.owner.username || page.canonicalHandle,
    isUaePassVerified: (mainCv ? mainCv.isUaePassVerified : page.owner.isUaePassVerified) === true,
    verificationScope: 'identity_name',
    careerClaimsVerified: false,
    name: mainCv?.name || page.owner.name,
    title: mainCv?.headline ?? page.owner.headline ?? null,
    summary: mainCv?.summary ?? page.summary ?? page.owner.bio ?? null,
    bio: page.owner.bio ?? null,
    location: mainCv?.location ?? page.owner.location ?? null,
    avatarUrl: mainCv?.avatarUrl ?? page.owner.avatarUrl ?? null,
    contactEmail: mainCv?.contactEmail ?? null,
    socials:
      mainCv?.socials && Object.keys(mainCv.socials).length > 0
        ? mainCv.socials
        : socials,
    openToWork: page.owner.openToWork === true,
    skills: page.skills,
    skillGroups: mainCv?.skillGroups ?? page.skillGroups ?? [],
    languages: mainCv?.languages ?? page.languages ?? [],
    highlights:
      mainCv?.highlights ??
      page.highlights.map((highlight) => ({
        title: highlight.title,
        desc: highlight.description ?? "",
      })),
    experiences: mainCv?.experiences ?? page.experiences,
    educations: mainCv?.educations ?? page.educations,
    certifications: mainCv?.certifications ?? page.certifications,
    projects: mainCv?.projects ?? page.projects,
    // Template/colour and the skill-applied section order travel with the
    // document: without them the app would render the owner's CV in default
    // styling and default section order, which is not the CV they published.
    // Null is fine: normalizePublicCvConfig below supplies the default.
    cvConfig: mainCv?.cvConfig ?? page.cvConfig ?? null,
    sectionsConfig: mainCv?.sectionsConfig ?? null,
    cv: mainCv
      ? {
          id: mainCv.id,
          title: mainCv.title,
          summary: mainCv.summary,
          slug: mainCv.slug,
          updatedAt: mainCv.updatedAt,
        }
      : null,
  };
}

export function normalizePublicCvConfig(
  config?: CVConfigData | null,
): CVConfig {
  if (!config) return DEFAULT_CV_CONFIG;
  return {
    style: config.style,
    color: config.color,
    showPhoto: config.showPhoto,
  };
}

function formatMonthYear(value: string | Date | null | undefined) {
  // Precision-aware (C13): a bare year stays a year, never "Jan 2019".
  return formatCvDateLabel(value, "en");
}
